import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ArrowLeftIcon, MagnifyingGlassIcon, UserIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { FormAlert } from '../components/form';
import CurrencyViewToggle from '../components/CurrencyViewToggle';
import PillGroup from '../components/PillGroup';
import { hasMultimoneda } from '../utils/multimoneda';
import { formatMoney } from '../utils/money';
import {
  VISTA_CONSOLIDADO,
  displayCurrency,
  shouldShowCurrencyToggle,
  vistaHint,
} from '../utils/currencyView';
import useStickyMonedas from '../hooks/useStickyMonedas';

const PERIODOS = [
  { id: 'semana', label: 'Semana' },
  { id: 'mes', label: 'Mes' },
  { id: 'trimestre', label: 'Trimestre' },
  { id: 'año', label: 'Año' },
  { id: 'todas', label: 'Todo' },
];

const PERIODO_TEXTO = {
  semana: 'la última semana',
  mes: 'este mes',
  trimestre: 'este trimestre',
  año: 'este año',
  todas: 'todo el historial',
};

const SLICE_COLORS = ['#1F80FF', '#004AAD', '#5CA3FF', '#3B82F6', '#2563EB', '#94A3B8'];

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

const DistribucionClientesDetalle = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroFecha, setFiltroFecha] = useState('año');
  const [currencyVista, setCurrencyVista] = useState(
    () => location.state?.currencyVista || VISTA_CONSOLIDADO
  );

  const currency = displayCurrency(currencyVista);

  const fetchClientes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(
        `/api/invoices-dashboard/top-clientes?periodo=${filtroFecha}&limit=200&vista=${encodeURIComponent(currencyVista)}`
      );
      if (!res.ok) throw new Error('Error al cargar clientes');
      const data = await res.json();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('No se pudo cargar la distribución de clientes.');
    } finally {
      setLoading(false);
    }
  }, [token, filtroFecha, currencyVista, secureFetch]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  const availableMonedas = useStickyMonedas(clientes, currencyVista);
  const showCurrencyToggle = hasMultimoneda(user) && shouldShowCurrencyToggle(availableMonedas);

  const ranked = useMemo(() => {
    return [...clientes].sort((a, b) => (b.total || 0) - (a.total || 0));
  }, [clientes]);

  const totalIngresos = ranked.reduce((sum, c) => sum + (c.total || 0), 0);
  const totalClientes = ranked.length;
  const topCliente = ranked[0];
  const topShare = totalIngresos > 0 && topCliente ? (topCliente.total / totalIngresos) * 100 : 0;
  const top3Share = totalIngresos > 0
    ? (ranked.slice(0, 3).reduce((sum, c) => sum + (c.total || 0), 0) / totalIngresos) * 100
    : 0;

  const concentracion80 = useMemo(() => {
    if (!totalIngresos) return 0;
    let acc = 0;
    let n = 0;
    for (const c of ranked) {
      acc += c.total || 0;
      n += 1;
      if (acc >= totalIngresos * 0.8) return n;
    }
    return n;
  }, [ranked, totalIngresos]);

  const chartData = useMemo(() => {
    if (!ranked.length) return [];
    const top = ranked.slice(0, 5);
    const restoTotal = ranked.slice(5).reduce((sum, c) => sum + (c.total || 0), 0);
    const slices = top.map((c, i) => ({
      name: c.cliente || 'Sin cliente',
      total: c.total || 0,
      color: SLICE_COLORS[i],
    }));
    if (restoTotal > 0) {
      slices.push({ name: 'Resto', total: restoTotal, color: SLICE_COLORS[5] });
    }
    return slices;
  }, [ranked]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return ranked;
    return ranked.filter((c) => {
      const name = (c.cliente || '').toLowerCase();
      const pais = (c.pais || '').toLowerCase();
      return name.includes(q) || pais.includes(q);
    });
  }, [ranked, searchTerm]);

  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const inputBg = modoOscuro
    ? 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500'
    : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400';

  const openCliente = (nombre) => {
    if (!nombre || nombre === 'Resto') return;
    navigate(`/facturas?cliente=${encodeURIComponent(nombre)}`);
  };

  const tooltipStyle = {
    backgroundColor: modoOscuro ? '#111827' : '#ffffff',
    border: `1px solid ${modoOscuro ? '#374151' : '#E5E7EB'}`,
    borderRadius: '10px',
    fontSize: '12px',
  };

  const kpis = [
    { label: 'Clientes', value: loading ? '—' : String(totalClientes), hint: 'con ventas en el período' },
    { label: 'Ingresos', value: loading ? '—' : formatMoney(totalIngresos, currency), hint: currencyVista === VISTA_CONSOLIDADO ? 'todas las monedas' : `solo ${currency}` },
    { label: 'Top 1', value: loading ? '—' : `${topShare.toFixed(0)}%`, hint: topCliente?.cliente || 'sin datos' },
    { label: 'Top 3', value: loading ? '—' : `${top3Share.toFixed(0)}%`, hint: 'de los ingresos' },
    { label: 'Hasta el 80%', value: loading ? '—' : String(concentracion80), hint: concentracion80 === 1 ? 'cliente concentra el 80%' : 'clientes llegan al 80%' },
  ];

  return (
    <div className={modoOscuro ? 'w-full bg-black text-white' : 'w-full bg-[#f5f5f7] text-gray-900'}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-4 sm:py-5 lg:py-6 pb-12 sm:pb-16 space-y-4 sm:space-y-5">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/inicio')}
              className={`p-2 rounded-lg border shrink-0 ${modoOscuro ? 'border-gray-700 bg-gray-900 hover:bg-gray-800' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              aria-label="Volver al inicio"
            >
              <ArrowLeftIcon className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-semibold leading-tight ${textPri}`}>
                Distribución de clientes
              </h1>
              <p className={`text-sm mt-1 ${textSec}`}>
                Quién concentra las ventas de {PERIODO_TEXTO[filtroFecha] || 'este período'}
                {currencyVista === VISTA_CONSOLIDADO && showCurrencyToggle ? ' · total' : currencyVista !== VISTA_CONSOLIDADO ? ` · ${currency}` : ''}
              </p>
            </div>
          </div>
          {showCurrencyToggle && (
            <div className="flex flex-col items-end gap-1">
              <CurrencyViewToggle
                value={currencyVista}
                onChange={setCurrencyVista}
                monedas={availableMonedas}
                modoOscuro={modoOscuro}
              />
              <p className={`text-[11px] ${textMuted}`}>{vistaHint(currencyVista)}</p>
            </div>
          )}
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5 sm:gap-3">
          {kpis.map((card) => (
            <div key={card.label} className={`rounded-2xl border p-3.5 sm:p-4 ${box}`}>
              <div className={`text-[11px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
              <div className={`text-lg sm:text-xl font-semibold mt-0.5 truncate ${textPri}`}>{card.value}</div>
              <div className={`text-[11px] mt-0.5 truncate ${textMuted}`}>{card.hint}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          <section className={`lg:col-span-5 rounded-2xl border p-4 sm:p-5 ${box}`}>
            <div className="mb-3">
              <h2 className={`text-sm font-semibold ${textPri}`}>Top 5 + resto</h2>
              <p className={`text-xs mt-0.5 ${textMuted}`}>Participación sobre el total del período</p>
            </div>
            {loading ? (
              <div className={`h-[240px] rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
            ) : chartData.length === 0 ? (
              <div className="h-[240px] flex flex-col items-center justify-center text-center">
                <UserIcon className={`w-10 h-10 mb-2 ${textMuted}`} />
                <p className={`text-sm font-medium ${textPri}`}>Todavía no hay distribución</p>
                <p className={`text-xs mt-1 ${textMuted}`}>Registrá ventas para ver quién concentra el mes.</p>
              </div>
            ) : (
              <>
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        dataKey="total"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={56}
                        outerRadius={88}
                        paddingAngle={2}
                        isAnimationActive={false}
                      >
                        {chartData.map((slice) => (
                          <Cell key={slice.name} fill={slice.color} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [formatMoney(value, currency), 'Ingresos']}
                        contentStyle={tooltipStyle}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="mt-2 space-y-1.5">
                  {chartData.map((slice) => {
                    const pct = totalIngresos > 0 ? (slice.total / totalIngresos) * 100 : 0;
                    return (
                      <li key={slice.name} className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: slice.color }} />
                          <span className={`truncate ${textSec}`}>{slice.name}</span>
                        </span>
                        <span className={`tabular-nums font-semibold shrink-0 ${textPri}`}>{pct.toFixed(1)}%</span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </section>

          <section className={`lg:col-span-7 rounded-2xl border overflow-hidden ${box}`}>
            <div className={`p-4 sm:p-5 border-b space-y-3 ${modoOscuro ? 'border-gray-700/80' : 'border-gray-200'}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <PillGroup value={filtroFecha} onChange={setFiltroFecha} options={PERIODOS} modoOscuro={modoOscuro} />
              </div>
              <div className="relative">
                <MagnifyingGlassIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textMuted}`} />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por cliente o país"
                  className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${inputBg} focus:outline-none focus:ring-2 focus:ring-[#1F80FF]/30`}
                />
              </div>
            </div>

            <FormAlert type="error" className="mx-4 mt-4">{error}</FormAlert>

            {loading ? (
              <div className="p-4 sm:p-5 space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`h-14 rounded-xl animate-pulse ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <UserIcon className={`w-10 h-10 mx-auto mb-2 ${textMuted}`} />
                <p className={`text-sm font-medium ${textPri}`}>No hay clientes en este recorte</p>
                <p className={`text-xs mt-1 ${textMuted}`}>
                  {searchTerm ? 'Probá otro nombre o país.' : 'Registrá ventas para armar la distribución.'}
                </p>
              </div>
            ) : (
              <ul className={modoOscuro ? 'divide-y divide-gray-800' : 'divide-y divide-gray-100'}>
                {filtered.map((cliente, index) => {
                  const porcentaje = totalIngresos > 0 ? (cliente.total / totalIngresos) * 100 : 0;
                  const nombre = cliente.cliente || 'Sin cliente';
                  return (
                    <li key={`${nombre}-${index}`}>
                      <button
                        type="button"
                        onClick={() => openCliente(nombre)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                          modoOscuro ? 'hover:bg-gray-800/70' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`text-xs font-semibold w-5 shrink-0 ${textMuted}`}>{index + 1}</span>
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                          modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                        }`}>
                          {initials(nombre)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-semibold truncate ${textPri}`}>{nombre}</div>
                          <div className={`text-[11px] truncate ${textMuted}`}>
                            {cliente.cantidadTrabajos || 0} venta{(cliente.cantidadTrabajos || 0) !== 1 ? 's' : ''}
                            {cliente.pais ? ` · ${cliente.pais}` : ''}
                            {cliente.impagas > 0 ? ` · ${cliente.impagas} impaga${cliente.impagas !== 1 ? 's' : ''}` : ''}
                          </div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
                              <div
                                className="h-full rounded-full bg-[#1F80FF]"
                                style={{ width: `${Math.min(Math.max(porcentaje, 0), 100)}%` }}
                              />
                            </div>
                            <span className={`text-[11px] font-semibold tabular-nums w-10 text-right ${textMuted}`}>
                              {porcentaje.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>
                            {formatMoney(cliente.total, currency)}
                          </div>
                          {cliente.impagas > 0 && (
                            <div className={`text-[11px] ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
                              {formatMoney(cliente.montoImpagas, currency)} pendiente
                            </div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {!loading && filtered.length > 0 && (
          <p className={`text-xs ${textMuted}`}>
            Tocá un cliente para ver sus ventas. El recorte usa la misma vista de moneda que el inicio.
          </p>
        )}
      </div>
    </div>
  );
};

export default DistribucionClientesDetalle;
